import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@advanza/shadcn-ui/src/components/ui/popover'
import clsx from 'clsx'
import { useState } from 'react'
import { evaluateScoring } from './scoringRules'
import type { ProviderResult, ScoringMode } from './types'

interface ScoringBreakdownProps {
    provider: ProviderResult
    mode: ScoringMode
    cityUrl?: string
    guaranteedFactor?: number
}

const ScoringBreakdown = ({ provider, mode, cityUrl, guaranteedFactor }: ScoringBreakdownProps) => {
    const [open, setOpen] = useState(false)

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button className="cursor-pointer rounded px-1.5 py-0.5 font-mono text-sm text-foreground transition-colors hover:bg-grey-200">
                    {provider._score?.toFixed(3) ?? '-'}
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-72 bg-white p-0" align="end">
                {open && (
                    <ScoringContent
                        provider={provider}
                        mode={mode}
                        cityUrl={cityUrl}
                        guaranteedFactor={guaranteedFactor}
                    />
                )}
            </PopoverContent>
        </Popover>
    )
}

const ScoringContent = ({ provider, mode, cityUrl, guaranteedFactor }: ScoringBreakdownProps) => {
    const { results, total, isMultiplicative } = evaluateScoring(
        provider,
        mode,
        cityUrl,
        guaranteedFactor,
    )

    // _es_score is the original ES score before applySecondSortOnResults overwrites _score
    const esScore = provider._es_score ?? provider._score
    const wasSecondSorted = provider._es_score != null

    return (
        <>
            <div className="border-b px-3 py-2">
                <p className="text-sm font-medium">{provider.business_name}</p>
                <p className="text-[10px] text-muted-foreground">
                    {isMultiplicative ? 'Multiplicative' : 'Additive'} scoring ({mode})
                </p>
            </div>
            <div className="p-2">
                {results.map(({ rule, applies, value }) => {
                    const dimmed = !applies && rule.type === 'condition'
                    const displayValue = (() => {
                        if (dimmed && rule.type === 'condition') {
                            return isMultiplicative ? `×${rule.weight}` : `+${rule.weight}`
                        }
                        if (rule.type !== 'condition' && 'format' in rule && rule.format) {
                            return rule.format(value)
                        }
                        return isMultiplicative ? `×${value}` : `+${value}`
                    })()

                    return (
                        <div
                            key={rule.key}
                            className={clsx(
                                'flex items-center justify-between py-0.5 text-sm',
                                dimmed && 'opacity-30',
                            )}>
                            <span className="text-muted-foreground">{rule.label}</span>
                            <span
                                className={clsx(
                                    'font-mono',
                                    applies &&
                                        value !== 1 &&
                                        isMultiplicative &&
                                        (value < 1 ? 'text-red-600' : 'text-green-700'),
                                )}>
                                {displayValue}
                            </span>
                        </div>
                    )
                })}
                {!isMultiplicative && esScore != null && (
                    <div className="flex items-center justify-between py-0.5 text-sm opacity-50">
                        <span className="text-muted-foreground">Base query score</span>
                        <span className="font-mono">+{(esScore - total).toFixed(3)}</span>
                    </div>
                )}
                <div className="mt-1 flex flex-col gap-0.5 border-t pt-1 text-sm font-medium">
                    <div className="flex items-center justify-between">
                        <span>ES _score</span>
                        <span className="font-mono">{esScore?.toFixed(3) ?? '-'}</span>
                    </div>
                    {wasSecondSorted && (
                        <div className="flex items-center justify-between text-[10px] font-normal text-amber-600">
                            <span>After 2nd sort</span>
                            <span className="font-mono">{provider._score?.toFixed(3)}</span>
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}

export default ScoringBreakdown
